import { zodTransformFirestoreTimestampToDate } from "../../utils/zodUtils.mjs";
import { zodFlavorId } from "../ids.mjs";
import { z as z$1 } from "zod";

// src/models/vendorSync/syncPrimitives.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
/** Fixed vendor slot doc ids and vendorSync.vendor field */
const consultationVendorSchema = z$1.enum([
	"bridge",
	"canvas",
	"tellescope",
	"stripe",
	"zoom"
]);
const syncTargetStatusSchema = z$1.enum([
	"pending",
	"in_flight",
	"synced",
	"failed",
	"skipped",
	"cancelled"
]);
const syncTargetSchema = z$1.object({
	status: syncTargetStatusSchema,
	ourExternalId: z$1.string().optional(),
	nativeId: z$1.string().optional(),
	lastAttemptAt: firestoreDate.optional(),
	attemptCount: z$1.number().int().nonnegative().optional(),
	lastError: z$1.string().optional(),
	syncedAt: firestoreDate.optional(),
	vendorUpdatedAt: firestoreDate.optional(),
	lastEventId: z$1.string().optional()
});
const vendorTargetMapSchema = z$1.record(z$1.string(), syncTargetSchema);
const orchestrationRefSchema = z$1.object({
	collection: z$1.literal("consultations"),
	id: zodFlavorId()
});
/** Write-once apply result under `{objectCollection}/{syncId}/ops/{eventId}`. */
const vendorSyncOpStatusSchema = z$1.enum([
	"success",
	"skipped",
	"failed_retriable",
	"failed_permanent"
]);
const vendorSyncOpOperationSchema = z$1.enum([
	"upsert",
	"patch",
	"fetch_only",
	"noop"
]);
/** Binding intent on `{owner}/vendors/{vendor}` — cascade control plane. */
const vendorBindingIntentSchema = z$1.enum([
	"ensure",
	"none",
	"cancel",
	"bill"
]);
const vendorBindingSchema = z$1.object({
	syncId: z$1.string().min(1),
	intent: vendorBindingIntentSchema,
	desiredRevision: z$1.number().int().nonnegative(),
	/**
	* Opaque desire payload for this slot — per-event specialized schemas parse
	* per binding key (e.g. bridge consent `target.consentedAt`).
	*/
	target: z$1.record(z$1.string(), z$1.unknown()).optional()
});
/** SyncId-keyed binding map — e.g. `bindings.payments`. */
const vendorBindingMultiMapSchema = z$1.record(z$1.string(), vendorBindingSchema).superRefine((map, ctx) => {
	for (const [key, binding] of Object.entries(map)) if (binding.syncId !== key) ctx.addIssue({
		code: z$1.ZodIssueCode.custom,
		message: `binding syncId must equal map key (key=${key}, syncId=${binding.syncId})`
	});
});
/** Singular binding or syncId-keyed multi-map under one top-level key. */
const vendorBindingEntrySchema = z$1.union([vendorBindingSchema, vendorBindingMultiMapSchema]);
/** Extensible map — future keys e.g. `policy` → `bridge_policies`. */
const vendorBindingsMapSchema = z$1.record(z$1.string(), vendorBindingEntrySchema);
function looksLikeSingularVendorBinding(entry) {
	return typeof entry.intent === "string" && typeof entry.desiredRevision === "number" && typeof entry.syncId === "string";
}
function isVendorBindingMultiMap(entry) {
	if (typeof entry !== "object" || entry === null) return false;
	return !looksLikeSingularVendorBinding(entry);
}
function asVendorBindingList(entry) {
	if (!entry) return [];
	if (isVendorBindingMultiMap(entry)) return Object.values(entry);
	return [entry];
}
function getSingularVendorBinding(bindings, key) {
	const entry = bindings?.[key];
	if (!entry || isVendorBindingMultiMap(entry)) return;
	return entry;
}
function getVendorBindingMultiMap(bindings, key) {
	const entry = bindings?.[key];
	if (!entry || !isVendorBindingMultiMap(entry)) return;
	return entry;
}
function getVendorBindingList(bindings, key) {
	return asVendorBindingList(bindings?.[key]);
}
function flattenVendorBindingEntry(entry) {
	if (!entry) return {};
	if (isVendorBindingMultiMap(entry)) return entry;
	return { [entry.syncId]: entry };
}
/** True when syncId / intent / desiredRevision differ (including add/remove). */
function vendorBindingChanged(before, after) {
	if (!before && !after) return false;
	if (!before || !after) return true;
	return before.syncId !== after.syncId || before.intent !== after.intent || before.desiredRevision !== after.desiredRevision;
}
/** Bindings whose control fields changed between before/after vendor docs. */
function listChangedVendorBindings(before, after) {
	const keys = /* @__PURE__ */ new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
	const changed = [];
	for (const key of keys) {
		const beforeFlat = flattenVendorBindingEntry(before?.[key]);
		const afterFlat = flattenVendorBindingEntry(after?.[key]);
		const syncIds = /* @__PURE__ */ new Set([...Object.keys(beforeFlat), ...Object.keys(afterFlat)]);
		for (const syncId of syncIds) {
			const beforeBinding = beforeFlat[syncId];
			const afterBinding = afterFlat[syncId];
			if (vendorBindingChanged(beforeBinding, afterBinding)) changed.push({
				key,
				syncId,
				before: beforeBinding,
				after: afterBinding
			});
		}
	}
	return changed;
}
const vendorEventSourceSchema = z$1.enum([
	"webhook",
	"cascade",
	"reconcile",
	"admin",
	"sse"
]);
/** @deprecated Consult nested resolve-event lifecycle — patient path uses write-once ops. */
const vendorSyncResolveAttemptStatusSchema = z$1.enum([
	"in_progress",
	"success",
	"skipped",
	"failed_retriable",
	"failed_permanent"
]);
/** @deprecated Consult nested resolve-event lifecycle — use global `events/{eventId}`. */
const vendorSyncResolveEventStatusSchema = z$1.enum([
	"pending",
	"success",
	"skipped",
	"failed_retriable",
	"failed_permanent"
]);
/** Keys where target expects convergence (pending or in_flight). */
function syncTargetKeysNeedingConvergence(target) {
	return Object.entries(target).filter(([, t]) => t.status === "pending" || t.status === "in_flight").map(([k]) => k);
}
function observedMatchesTarget(target, observed, keys) {
	return (keys ?? syncTargetKeysNeedingConvergence(target)).every((key) => {
		const t = target[key];
		const o = observed[key];
		if (!t) return true;
		if (t.status === "skipped" || t.status === "cancelled") return true;
		if (!o) return false;
		return o.status === "synced";
	});
}
/** Stable op doc id for a target + operation pair. */
function generateVendorSyncOpKey(targetKey, operation) {
	return `${targetKey}_${operation}`;
}
function isVendorSyncResolveEventTerminalOk(status) {
	return status === "success" || status === "skipped";
}
/** Write-once ops: success/skipped/failed_permanent must not be retried. */
function isTerminalVendorSyncOp(existing) {
	return existing?.status === "success" || existing?.status === "skipped" || existing?.status === "failed_permanent";
}

export { asVendorBindingList, consultationVendorSchema, generateVendorSyncOpKey, getSingularVendorBinding, getVendorBindingList, getVendorBindingMultiMap, isTerminalVendorSyncOp, isVendorBindingMultiMap, isVendorSyncResolveEventTerminalOk, listChangedVendorBindings, observedMatchesTarget, orchestrationRefSchema, syncTargetKeysNeedingConvergence, syncTargetSchema, syncTargetStatusSchema, vendorBindingChanged, vendorBindingEntrySchema, vendorBindingIntentSchema, vendorBindingMultiMapSchema, vendorBindingSchema, vendorBindingsMapSchema, vendorEventSourceSchema, vendorSyncOpOperationSchema, vendorSyncOpStatusSchema, vendorSyncResolveAttemptStatusSchema, vendorSyncResolveEventStatusSchema, vendorTargetMapSchema };
//# sourceMappingURL=syncPrimitives.mjs.map